/**
 * Sidebar website picker.
 *
 * Phase 1: shows each website's favicon next to its label so users can
 * recognise their workspaces at a glance. Native <select> can't render
 * images, so this is a lightweight custom dropdown.
 *
 * Stores the chosen website ID in localStorage so the user lands on the
 * same workspace on next visit.
 */

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { websites as websitesApi } from "../api/endpoints";
import SiteFavicon from "./SiteFavicon.jsx";

const KEY = "seogeeks:lastWebsiteId";

export default function WebsitePicker({ onChange }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(
    () => Number(localStorage.getItem(KEY)) || null,
  );
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    websitesApi.list().then((res) => {
      setItems(res.data);
      if (res.data.length > 0) {
        const stillExists = res.data.find((w) => w.id === selected);
        const next = stillExists?.id ?? res.data[0].id;
        if (next !== selected) {
          setSelected(next);
          localStorage.setItem(KEY, String(next));
        }
        onChange?.(next);
      } else {
        onChange?.(null);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    function handleClick(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    function handleEsc(e) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleEsc);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [open]);

  function pick(w) {
    setSelected(w.id);
    localStorage.setItem(KEY, String(w.id));
    onChange?.(w.id);
    setOpen(false);
    navigate("/dashboard");
  }

  if (items.length === 0) {
    return (
      <button
        type="button"
        onClick={() => navigate("/dashboard/websites")}
        className="w-full rounded-md bg-brand-600 text-white text-sm py-2 hover:bg-brand-700"
      >
        {t("nav.addFirstWebsite")}
      </button>
    );
  }

  const current = items.find((w) => w.id === selected) || items[0];

  return (
    <div className="relative" ref={wrapperRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 rounded-md border border-slate-300 bg-white text-sm py-2 px-3 hover:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-500"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <SiteFavicon
          url={current.favicon_url}
          domain={current.domain}
          label={current.label}
          size={18}
        />
        <span className="truncate flex-1 text-left text-slate-900">{current.label}</span>
        <span className="text-slate-400 text-xs">▾</span>
      </button>
      {open && (
        <ul
          role="listbox"
          className="absolute z-20 mt-1 max-h-72 w-full overflow-auto rounded-md border border-slate-200 bg-white shadow-lg ring-1 ring-black/5"
        >
          {items.map((w) => (
            <li key={w.id}>
              <button
                type="button"
                onClick={() => pick(w)}
                className={[
                  "w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-brand-50",
                  w.id === selected ? "bg-brand-50 font-medium" : "",
                ].join(" ")}
              >
                <SiteFavicon
                  url={w.favicon_url}
                  domain={w.domain}
                  label={w.label}
                  size={18}
                />
                <span className="truncate">{w.label}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
